import * as React from 'react';
import { useIntl } from 'react-intl';
import { Control, useController } from 'react-hook-form';
import { CmdError } from '@commander-services/gui-components';
import TooltipComponent from '../../TooltipComponent';
import SearchService from '../../../Services/SearchService';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';
import './groupChipSelectUniqueStyles.scss';

export interface IGroupChipSelectOptions {
  [groupName: string]: IGroupChipSelectOption[];
}

export interface IGroupChipSelectOption {
  item: string;
  value: string | number;
  disabled?: boolean;
  groupName?: string;
  passed?: boolean;
  popup?: null | string;
  vehicles?: string;
  total?: string;
  customerLabel?: string;
}

interface ISelectedGroupChipItems {
  [groupName: string]: string[];
}

interface IGroupChipSelectProps {
  id: string;
  name: string;
  label: string;
  options: IGroupChipSelectOptions;
  searchPlaceholder: string;
  selectButton: string;
  resetButton: string;
  control: Control<any>;
  placeholder?: string;
  required?: boolean;
  onChangeCallback?: (number: number, values: (string | number)[]) => void;
  showDetail?: () => void;
  showPartialDetail?: (id: string) => void;
  itemsNumber?: number;
  // selectedValues?: ISelectedGroupChipItems;
  searchTooltip?: string;
  selectButtonTooltip?: string;
  resetButtonTooltip?: string;
  tooltip?: string;
  warningMessage?: string;
}

export default function GroupChipSelectUniqueHookForm(props: IGroupChipSelectProps): JSX.Element {
  const { formatMessage: f } = useIntl();

  const initSelectedItems = (options: IGroupChipSelectOptions): (string | number)[] => {
    const selectedItems: (string | number)[] = [];
    Object.keys(options).forEach((groupName: string) => {
      options[groupName].forEach((optionItem: IGroupChipSelectOption) => {
        selectedItems.push(optionItem.value);
      });
    });
    return selectedItems;
  };

  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = React.useState<boolean>(false);
  const [searchString, setSearchString] = React.useState<string>('');
  const [optionsFromSearch, setOptionsFromSearch] = React.useState<IGroupChipSelectOptions>(
    props.options
  );
  const [isTooltipDisabled, setIsTooltipDisabled] = React.useState<boolean>(false);

  const {
    field: { ref, onChange, onBlur, value },
    fieldState: { invalid, error },
  } = useController({
    name: props.name,
    control: props.control,
    defaultValue: '', // or the default value
  });

  const [selectedItems, setSelectedItems] = React.useState<(string | number)[]>(
    value && Object.keys(value).length > 0 ? value : []
  );

  React.useEffect(() => {
    setOptionsFromSearch(props.options);

    if (value) {
      setSelectedItems(value);
    }
  }, [props.options, value]);

  const clickOutsideRef: React.RefObject<any> = React.useRef();

  useOutsideClick(clickOutsideRef, () => {
    setIsDropdownMenuOpen(false);
  });
  useKeyPress(ESCAPE_KEY, () => {
    setIsDropdownMenuOpen(false);
  });

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: eventValue } = event.currentTarget;
    setSearchString(eventValue);

    const filteredGroups: IGroupChipSelectOptions = {};
    if (props.options) {
      Object.keys(props.options).forEach((key: string) => {
        const filtered = props.options[key].filter(
          (option: IGroupChipSelectOption) =>
            SearchService.removeAccent(option.item.toLowerCase()).includes(
              SearchService.removeAccent(eventValue.toLowerCase())
            ) ||
            (option.customerLabel &&
              SearchService.removeAccent(option.customerLabel.toLowerCase()).includes(
                SearchService.removeAccent(eventValue.toLowerCase())
              ))
        );
        if (filtered.length > 0) {
          filteredGroups[key] = filtered;
        }
      });
    }
    if (eventValue) {
      setOptionsFromSearch(filteredGroups);
    } else {
      setOptionsFromSearch(props.options);
    }
  };

  const handleDetail = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.showDetail) {
      props.showDetail();
    } else {
      setIsDropdownMenuOpen(!isDropdownMenuOpen);
    }
  };

  const handlePartialDetail = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const id = event.currentTarget.getAttribute('data-id');
    event.preventDefault();
    event.stopPropagation();
    if (props.showPartialDetail) {
      props.showPartialDetail(String(id));
    } else {
      setIsDropdownMenuOpen(!isDropdownMenuOpen);
    }
  };

  const handleShowDropdownMenu = (
    event:
      | React.MouseEvent<HTMLLabelElement, MouseEvent>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsDropdownMenuOpen(!isDropdownMenuOpen);
  };

  const handleReset = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    setSelectedItems(initSelectedItems(props.options));
    onChange([]);
  };

  const handleApply = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    onChange(selectedItems);
    setIsDropdownMenuOpen(false);

    if (props.onChangeCallback) {
      props.onChangeCallback(selectedItems.length, selectedItems);
    }
  };

  const handleSelectItem = (
    event: React.MouseEvent<HTMLLabelElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    const dataValue = event.currentTarget.getAttribute('data-value');
    const groupName = event.currentTarget.getAttribute('data-group');
    const disabled = event.currentTarget.getAttribute('data-disabled');

    if (disabled === 'true') {
      return;
    }

    if (!groupName) {
      return;
    }

    if (dataValue) {
      if (!selectedItems.includes(dataValue)) {
        const newSelectedItems: (string | number)[] = [...selectedItems, dataValue];
        setSelectedItems(newSelectedItems);
      } else {
        const newSelectedItems = selectedItems.filter(
          (item: string | number) => item !== dataValue
        );
        setSelectedItems(newSelectedItems);
      }
    }
  };

  const handleRemoveItem = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    const selectedValue = event.currentTarget.getAttribute('data-value');
    if (selectedValue) {
      const newSelectedItems = selectedItems.filter(
        (item: string | number) => item !== selectedValue
      );
      const countItems = newSelectedItems.length;
      if (countItems === 0) {
        // setFieldTouched(props.name);
        setSelectedItems(newSelectedItems);
        onChange([]);
      } else {
        setSelectedItems(newSelectedItems);
        onChange(newSelectedItems);
      }
    }
  };

  const getSelectedFieldValues = (fieldValue, options: IGroupChipSelectOptions) => {
    const selectedFieldValueItems: IGroupChipSelectOption[] = [];
    if (fieldValue) {
      if (fieldValue.length > 0 && options) {
        Object.keys(options).forEach((groupName: string) => {
          options[groupName]
            .filter((optionItem: IGroupChipSelectOption) =>
              fieldValue.includes(String(optionItem.value))
            )
            .forEach((optionItem: IGroupChipSelectOption) => {
              const newOptionItem = {
                ...optionItem,
                groupName,
              };
              selectedFieldValueItems.push(newOptionItem);
            });
        });
      }
      return selectedFieldValueItems;
    }
    return [];
  };

  const selectedFieldValueItems: IGroupChipSelectOption[] = getSelectedFieldValues(
    value,
    props.options
  );

  const getErrorItems = () => {
    let isError = false;
    const numberOfseenItems = props.itemsNumber || 2;
    Object.values(selectedFieldValueItems).forEach((item: any, key: number) => {
      if (key > numberOfseenItems - 1) {
        if (item.passed === false) {
          isError = true;
        }
      }
    });
    return isError;
  };

  const handleOnMouseEnter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setIsTooltipDisabled(true);
  };

  const handleOnMouseLeave = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setIsTooltipDisabled(false);
  };

  return (
    <div
      ref={clickOutsideRef}
      className={`group-chip-select-component form-group ${
        props.warningMessage && 'form-group-warning'
      } ${error && 'form-group-error'}`}
    >
      <div>
        <label className="d-block form-label" onClick={handleShowDropdownMenu}>
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
        <div className="d-block">
          <div id={props.id} className={`b-dropdown ${isDropdownMenuOpen ? 'open' : ''}`}>
            <div
              id={`choose-${props.id}`}
              className="form-control-labels"
              onClick={handleShowDropdownMenu}
              title={props.tooltip && !isTooltipDisabled ? props.tooltip : ''}
              data-cy="groupChipSelect-choose"
            >
              {selectedFieldValueItems.length > 0 ? (
                <>
                  {(selectedFieldValueItems.length > 0 ||
                    selectedFieldValueItems.length < (props.itemsNumber || 2)) &&
                    selectedFieldValueItems
                      .filter(
                        (_optionItem: IGroupChipSelectOption, index: number) =>
                          index < (props.itemsNumber || 2)
                      )
                      .map((optionItem: IGroupChipSelectOption, indexMap: number) =>
                        optionItem.popup ? (
                          <TooltipComponent
                            id={props.id}
                            firstItems={true}
                            positionLeft={true}
                            overlay={<div className="overlay_color">{optionItem.popup}</div>}
                            key={indexMap}
                            data-cy={`tooltip-${props.name}-${optionItem.popup}`}
                          >
                            <div
                              onClick={handlePartialDetail}
                              onMouseEnter={handleOnMouseEnter}
                              onMouseLeave={handleOnMouseLeave}
                              data-id={optionItem.value}
                              className={`label ${
                                optionItem.passed === false ? 'label-error' : ''
                              }`}
                              key={`label-${optionItem.item}`}
                              data-cy={`chips-false-${props.name}-${optionItem.value}`}
                              style={{ paddingLeft: optionItem.passed === false ? '30px' : '12px' }}
                            >
                              {optionItem.passed === false && (
                                <span style={{ position: 'absolute', top: 0, left: '9px' }}>
                                  <i
                                    id={`icon-${optionItem.item}`}
                                    className="cmd-icon cmd-icon-exclamation"
                                  />
                                  &nbsp;&nbsp;&nbsp;
                                </span>
                              )}
                              {`${optionItem.item} `}
                              <a
                                className="label-action cmd-icon cmd-icon-remove-sm"
                                data-value={optionItem.value}
                                data-group={optionItem.groupName}
                                onClick={handleRemoveItem}
                                data-cy={`remove-false-chips-${props.name}-${optionItem.value}`}
                              >
                                {/* Content */}
                              </a>
                            </div>
                          </TooltipComponent>
                        ) : (
                          <div
                            onClick={handlePartialDetail}
                            data-id={optionItem.value}
                            className={`label ${
                              optionItem.passed === false ? 'label-error' : ''
                            }`}
                            key={`label-${optionItem.item}`}
                            data-cy={`chips-${props.name}-${optionItem.value}`}
                            style={{ paddingLeft: optionItem.passed === false ? '30px' : '12px' }}
                          >
                            {optionItem.passed === false && (
                              <span style={{ position: 'absolute', top: 0, left: '9px' }}>
                                <i
                                  id={`icon-${optionItem.item}`}
                                  className="cmd-icon cmd-icon-exclamation"
                                />
                                &nbsp;&nbsp;&nbsp;
                              </span>
                            )}
                            {`${optionItem.item} `}
                            <a
                              className="label-action cmd-icon cmd-icon-remove-sm"
                              data-value={optionItem.value}
                              data-group={optionItem.groupName}
                              onClick={handleRemoveItem}
                              data-cy={`remove-chips-${props.name}-${optionItem.value}`}
                            >
                              {/* Content */}
                            </a>
                          </div>
                        )
                      )}
                  {selectedFieldValueItems.length > (props.itemsNumber || 2) && (
                    <TooltipComponent
                      id={props.id}
                      firstItems={true}
                      positionLeft={true}
                      overlay={
                        <div className="overlay_color">
                          {getErrorItems()
                            ? f({ id: 'alarms.modal.vehciles.error_items' })
                            : f({ id: 'alarms.modal.vehciles.all_items' })}
                        </div>
                      }
                      key="err"
                      data-cy={`tooltip-${props.name}`}
                    >
                      <div
                        onClick={handleDetail}
                        className={`label ${getErrorItems() ? 'label-error' : ''}`}
                        style={{ paddingLeft: getErrorItems()  ? '30px' : 0 }}
                      >
                        {getErrorItems() && (
                          <span style={{ position: 'absolute', top: 0, left: '9px' }}>
                            <i
                              id="main-icon"
                              className="cmd-icon cmd-icon-exclamation"
                              data-cy={`exlamation-icon-${props.name}`}
                            />
                            &nbsp;&nbsp;&nbsp;
                          </span>
                        )}
                        +{selectedFieldValueItems.length - (props.itemsNumber || 2)}
                      </div>
                    </TooltipComponent>
                  )}
                </>
              ) : (
                <span>{props.placeholder}</span>
              )}
            </div>
            {isDropdownMenuOpen && (
              <div className="b-dropdown__content">
                <div id={`group-chip-select-unique-${props.id}`} className="b-dropdown__header">
                  <input
                    type="text"
                    className="form-control"
                    onChange={handleSearch}
                    value={searchString}
                    placeholder={props.searchPlaceholder}
                    autoFocus={true}
                    data-cy={`group-chip-select-unique-search-${props.id}`}
                    title={props.searchTooltip || ''}
                  />
                </div>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {Object.keys(optionsFromSearch).map((groupName: string) => (
                    <div key={`${groupName}`}>
                      <div id={`title-${props.id}`} className="b-dropdown__title">
                        {groupName}
                      </div>
                      <ul id={`multipleCheckboxes-${props.id}`} className="b-dropdown__list">
                        {optionsFromSearch[groupName].map(
                          (option: IGroupChipSelectOption, index: number) => (
                            <li key={`${groupName}-${option.item}-${index}`}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <div className="e-checkbox" style={{ flex: '1' }}>
                                  <label
                                    onClick={handleSelectItem}
                                    id={`${props.id}_selectCheckbox`}
                                    data-cy={`group-select-multiple-label-${option.value}`}
                                    data-value={option.value}
                                    data-group={groupName}
                                    data-disabled={option.disabled}
                                  >
                                    <input
                                      type="checkbox"
                                      value={option.value}
                                      disabled={option.disabled || false}
                                      checked={
                                        selectedItems
                                          ? selectedItems.includes(String(option.value))
                                          : false
                                      }
                                      data-value={option.value}
                                      data-key={groupName}
                                      onChange={handleSelectItem}
                                      data-cy={`checkbox-${props.name}-${option.value}`}
                                    />
                                    <span id={props.id}>{option.item}</span>
                                  </label>
                                  {Number(option.vehicles) > -1 && Number(option.total) > -1 ? (
                                    <span
                                      className={`${option.disabled ? 'span_color' : ''}`}
                                      // style={option.disabled ? { color: '#a6a6a6' } : { color: '' }}
                                      data-cy={`information-${props.name}-${option.value}`}
                                    >
                                      {' '}
                                      ({option.vehicles}/{option.total})&nbsp;
                                    </span>
                                  ) : (
                                    <span />
                                  )}
                                  {option.passed === false && (
                                    <i
                                      id={`icon-${props.id}`}
                                      className="cmd-icon cmd-icon-exclamation text-primary"
                                      data-cy={`exlamation-icon-${props.name}`}
                                    />
                                  )}
                                </div>{' '}
                                <div style={{ justifyContent: 'flex-end' }}>
                                  {option.customerLabel && option.customerLabel}
                                </div>
                              </div>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
                <div id={`footer-${props.id}`} className="b-dropdown__footer">
                  <button
                    id={`reset-${props.id}`}
                    data-cy={`reset-${props.id}`}
                    className="e-button"
                    onClick={handleReset}
                    title={props.resetButtonTooltip || ''}
                  >
                    {props.resetButton}
                  </button>
                  <button
                    id={`apply-${props.id}`}
                    data-cy={`apply-${props.id}`}
                    className="e-button e-button--gray"
                    onClick={handleApply}
                    title={props.selectButtonTooltip || ''}
                  >
                    {props.selectButton}
                  </button>
                </div>
              </div>
            )}
          </div>
          {error && <CmdError message={error.message} />}
          {props.warningMessage && <CmdError message={props.warningMessage} />}
        </div>
      </div>
    </div>
  );
}
